import { sendRequest, Query } from 'best-modules/utils'

import type {
  PaginationList,
  Report,
  Package,
  PackageData,
  PackageCredentials,
  PersonFullSchemaCredentials,
  PersonShortSchemaCredentials,
  PersonFull,
  PersonShort,
  RelatedPerson,
  PackageParseType,
  PackageParseResult,
  PackageReplaceCredentials,
  PrintedFormResult,
} from '@/utils/types'
import {
  PackageDirectoryItem,
  PackageDirectoryName,
} from '@/store/package-directory/types'
import { download, getFile } from 'best-modules/utils/fileHandlers'
import { Url } from 'best-modules/utils/request/url'

const getReportList = (
  query: Query<'page' | 'month' | 'year'>
): Promise<PaginationList<Report>> => {
  return sendRequest({
    name: 'reportList',
    url: { url: '/admin/report/getAll', query },
  })
}

const getReportSingle = (reportId: string): Promise<Report> => {
  return sendRequest({
    name: 'reportSingle',
    url: `/admin/report/single/${reportId}`,
  })
}

const createReport = (report: {
  year: number
  month: number
  version: number
}): Promise<Report> => {
  const request = {
    period: `${report.year}-${String(report.month).padStart(2, '0')}-01`,
    version: report.version,
  }
  return sendRequest({
    name: 'reportCreate',
    url: '/admin/report/create',
    method: 'post',
    payload: request,
  })
}
const updateReport = (report: Report): Promise<Report> => {
  return sendRequest({
    name: 'reportUpdate',
    url: `/admin/report/update/${report.id}`,
    method: 'put',
    payload: report,
  })
}
const analyzeReport = (credentials: {
  previousReportId: string
  currentReportId: string
}): Promise<void> => {
  return sendRequest({
    name: 'analyzeReport',
    method: 'post',
    url: '/admin/report/analyze',
    payload: credentials,
  })
}

const financialAnalyzeReport = (reportId: number): Promise<void> => {
  return sendRequest({
    name: 'financialAnalyzeReport',
    method: 'post',
    url: '/admin/report/financialAnalyze',
    payload: { reportId },
  })
}
const detailReport = (reportId: number): Promise<void> => {
  return sendRequest({
    name: 'detailReport',
    method: 'post',
    url: '/admin/report/detail',
    payload: { reportId },
  })
}
const debtorDuplicatesReport = (payload: {
  reportId: number
  statusIds: number[]
  duplicateType: string
}): Promise<void> => {
  return sendRequest({
    name: 'debtorDuplicatesReport',
    method: 'post',
    url: '/admin/report/debtorDuplicate',
    payload: payload,
  })
}

const debtorChronologyReport = (debtorId: string): Promise<void> => {
  return sendRequest({
    name: 'debtorChronologyReport',
    method: 'post',
    url: '/admin/report/debtorChronology',
    payload: { debtorId },
  })
}

const analyzeReportResult = (): Promise<[PrintedFormResult] | []> => {
  return sendRequest({
    name: 'analyzeReportResult',
    method: 'get',
    url: '/admin/resultPrintedForm',
  })
}

const getReportPackageList = (
  reportId: string,
  query: Query<'page' | 'statusId' | 'authorId'>
): Promise<PaginationList<Report>> => {
  return sendRequest({
    name: 'packageList',
    url: { url: `/admin/report/${reportId}/package/getAll`, query },
  })
}

const getPackageSingle = (packageId: string): Promise<Package> => {
  return sendRequest({
    name: 'packageSingle',
    url: `/admin/report/package/single/${packageId}`,
  })
}

const getPackageSchemaBody = (
  version: number
): Promise<Omit<PackageData, 'person_short' | 'person_full'>> => {
  return sendRequest({
    name: 'packageSchemaBody',
    url: {
      url: '/admin/template/schema/body',
      query: { version },
    },
    method: 'get',
  })
}
const updateValidationsSchemas = () => {
  return sendRequest({
    name: 'updateValidationsSchemas',
    url: '/admin/template/validationsSchemas/update',
    method: 'get',
  })
}
const getPackageSchemaPersonFull = (
  credentials: PersonFullSchemaCredentials,
  version: number
): Promise<{ person_full: [PersonFull] }> => {
  return sendRequest({
    name: 'packageSchemaPersonFull',
    method: 'post',
    url: {
      url: '/admin/template/schema/personFull',
      query: { version },
    },
    payload: credentials,
  })
}
const getPackageSchemaPersonShort = (
  credentials: PersonShortSchemaCredentials,
  version: number
): Promise<{
  person_short: [PersonShort]
  related_person: [RelatedPerson]
}> => {
  return sendRequest({
    name: 'packageSchemaPersonShort',
    method: 'post',
    url: {
      url: '/admin/template/schema/personShort',
      query: { version },
    },
    payload: credentials,
  })
}

const getUserCompanyPersonShort = (): Promise<PersonShort> => {
  return sendRequest({
    name: 'userCompanyPersonShort',
    method: 'get',
    url: '/admin/company/json',
  })
}

const createPackage = (
  packageData: PackageCredentials & { createType: 'W' | 'E' | 'J' }
): Promise<Package> => {
  console.log(packageData)
  return sendRequest({
    name: 'packageCreate',
    url: '/admin/report/package/create',
    method: 'post',
    payload: {
      reportId: packageData.reportId,
      data: packageData.data,
      createType: packageData.createType,
    },
  })
}

const updatePackage = (
  packageData: PackageData,
  packageId: string
): Promise<Package> => {
  console.log('updatePackageRequest', packageData)
  return sendRequest({
    name: 'packageUpdate',
    url: `/admin/report/package/update/${packageId}`,
    method: 'put',
    payload: {
      data: packageData,
    },
  })
}

const sendPackage = packages => {
  return sendRequest({
    name: 'sendPackage',
    url: '/admin/nbu/send',
    method: 'post',
    payload: {
      packages,
    },
  })
}

const deletePackage = (arr: any[]) => {
  if (arr.length) {
    return sendRequest({
      name: 'packageDelete',
      url: '/admin/report/package/delete',
      method: 'delete',
      payload: { reportPackagesId: arr },
    })
  } else {
    return Promise.reject(new Error('Array is empty'))
  }
}

const getPackageTemplate = (version: number) => {
  return sendRequest({
    name: 'getPackageTemplate',
    url: { url: '/admin/template/excel', query: { version } },
    method: 'get',
  })
}

const getPackageDomains = () => {
  return sendRequest({
    name: 'getPackageDomains',
    url: '/admin/template/domains',
    method: 'get',
  })
}
const downloadReceipt = (
  reportPackagesId: string[]
): Promise<{ url: string; name: string }> => {
  return sendRequest({
    name: 'downloadReceipt',
    url: '/admin/report/package/receipt',
    method: 'post',
    payload: {
      reportPackagesId,
    },
  })
}
const downloadReceiptByReport = (
  reportId: string
): Promise<{ url: string; name: string }> => {
  return sendRequest({
    name: 'downloadReceiptByReport',
    url: '/admin/report/detail',
    method: 'post',
    payload: {
      reportId,
    },
  })
}

const copyPackage = (
  reportId: string,
  packageId: string[],
  regenerateUUID: boolean
) => {
  const req = {
    reportId: reportId,
    reportPackagesId: packageId,
    regenerateUUID,
  }
  return sendRequest({
    name: 'packageCopy',
    url: '/admin/report/package/copy',
    method: 'post',
    payload: req,
  })
}

const parsePackage = (
  parseData: {
    file: string
  },
  parseFrom: PackageParseType
): Promise<PackageParseResult> => {
  const parseTypes: Record<PackageParseType, Url> = {
    excel: '/admin/report/package/parse',
    json: '/admin/report/package/json/parse',
  }
  const url = parseTypes[parseFrom]
  return sendRequest({
    name: 'packageParse',
    url,
    method: 'post',
    payload: parseData,
  })
}

const replacePackage = (
  packageId: string,
  credentials: PackageReplaceCredentials
): Promise<Package> => {
  return sendRequest({
    name: 'packageReplace',
    url: `/admin/report/package/replace/${packageId}`,
    method: 'put',
    payload: credentials,
  })
}

const packageValidation = (packageId: string) => {
  return sendRequest({
    name: 'packageParseValidation',
    url: `/admin/report/package/validation/${packageId}`,
    method: 'get',
  })
}

const getPackageDirectory = (): Promise<
  Record<PackageDirectoryName, PackageDirectoryItem>
> => {
  return sendRequest({
    name: 'getPackageDirectory',
    url: '/admin/list/catalogs ',
  }).then(
    (res: { data: Record<PackageDirectoryName, PackageDirectoryItem> }) => {
      return res.data
    }
  )
}

const sendPackageToRevision = (packageId: string): Promise<Package> => {
  return sendRequest({
    name: 'sendPackageToRevision',
    url: '/admin/report/package/revision',
    method: 'post',
    payload: {
      reportPackageId: packageId,
    },
  })
}

const exportValidation = (reportPackagesId: string[]): Promise<void> => {
  return sendRequest({
    name: 'exportValidation',
    method: 'post',
    url: '/admin/report/package/exportValidation',
    payload: { reportPackagesId },
  }).then(async (res: { file: string }) => {
    const file = (await getFile(res.file)) as string
    await download(file, res.file.split('/').at(-1))
  })
}

const exportPackage = (reportPackageId: string): Promise<void> => {
  return sendRequest({
    name: 'exportPackage',
    method: 'post',
    url: '/admin/report/package/json/export',
    payload: { reportPackageId },
  }).then(async (res: { file: string }) => {
    const file = (await getFile(res.file)) as string
    await download(file, res.file.split('/').at(-1))
  })
}

const exportReportPackages = (reportId: string): Promise<void> => {
  return sendRequest({
    name: 'exportReportPackages',
    method: 'post',
    url: '/admin/report/package/json/exportReport',
    payload: { reportId },
  }).then(async (res: { file: string }) => {
    const file = (await getFile(res.file)) as string
    await download(file, res.file.split('/').at(-1))
  })
}

const removePackageSignature = (packagesId: string[]): Promise<void> => {
  return sendRequest({
    name: 'removePackageSignature',
    method: 'post',
    url: '/admin/report/package/electronicSignature/unset',
    payload: { reportPackagesId: packagesId },
  })
}

export {
  getReportList,
  getReportSingle,
  getReportPackageList,
  createReport,
  updateReport,
  analyzeReport,
  financialAnalyzeReport,
  detailReport,
  debtorDuplicatesReport,
  debtorChronologyReport,
  analyzeReportResult,
  getPackageSingle,
  getPackageSchemaBody,
  getPackageSchemaPersonFull,
  getPackageSchemaPersonShort,
  getUserCompanyPersonShort,
  createPackage,
  updatePackage,
  deletePackage,
  getPackageTemplate,
  getPackageDomains,
  sendPackage,
  copyPackage,
  parsePackage,
  replacePackage,
  packageValidation,
  getPackageDirectory,
  updateValidationsSchemas,
  sendPackageToRevision,
  exportValidation,
  exportPackage,
  exportReportPackages,
  downloadReceipt,
  downloadReceiptByReport,
  removePackageSignature,
}
